// src/pages/AccountOverview/AccountOverview.jsx
import React from "react";
import { Row, Col, Badge, Button, Modal } from "react-bootstrap";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCircle,
  faMicrochip,
  faMobileScreen,
  faCrown,
} from "@fortawesome/free-solid-svg-icons";
import { openBillingPortal } from "../../lib/subscription";
import "./AccountOverview.css";
import PetPageHeader from "../../components/PetPageHeader";


export default function AccountOverview({ subInfo }) {
  const navigate = useNavigate();

  const [phone, setPhone] = React.useState("");
  const [tagId, setTagId] = React.useState("");

  // 🔴 Delete account modal state
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = React.useState("");

  React.useEffect(() => {
    async function load() {
      try {
        const user = await Auth.currentAuthenticatedUser();

        const rawTagId = user?.attributes?.preferred_username || "";
        setTagId(rawTagId.toUpperCase());

        setPhone(user.attributes.phone_number || "Not provided");
      } catch (e) {
        console.error("Failed loading user:", e);
      }
    }
    load();
  }, []);

  // 🧮 Normalize subscription / plan info
  const status = subInfo?.status || "none";
  const planName = subInfo?.planName || null;

  const rawMax =
    typeof subInfo?.maxTags === "number"
      ? subInfo.maxTags
      : Number(subInfo?.petLimit ?? 0);

  const maxTags = Number.isNaN(rawMax) ? 0 : rawMax;

  const usedTags = Number.isFinite(subInfo?.usedTags)
    ? subInfo.usedTags
    : Number(subInfo?.usedTags ?? 0);


  const atLimit = maxTags > 0 && usedTags > maxTags;

  const planSummaryText =
    status === "active" && planName
      ? `Plan: ${planName} · SmartTags in use: ${usedTags}${
          maxTags ? ` of ${maxTags}` : ""
        }`
      : "No active SmartTag plan detected.";

  const handleManageCurrentTag = () => {
    if (tagId) {
      navigate(`/smarttag/${encodeURIComponent(tagId)}`);
    } else {
      navigate("/smarttag");
    }
  };

  const handleLinkNewTag = () => {
    if (atLimit) {
      alert(
        "You’ve reached the SmartTag limit for your current plan. Upgrade your plan or remove an existing pet to add more."
      );
      return;
    }
    navigate("/smarttag");
  };

  const handleManagePlan = async () => {
    try {
      await openBillingPortal();
    } catch (err) {
      console.error("Billing portal error:", err);
      alert(
        "We couldn't open your subscription settings. If this keeps happening, contact support."
      );
    }
  };

  const handleDeleteProfile = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();

      const rawTagId = user?.attributes?.preferred_username || "";
      const lpId = (rawTagId || "").toUpperCase();

      //console.log("Deleting Cognito user account for SmartTag:", lpId);

      await Auth.deleteUser();

      localStorage.removeItem("lpp.tagId");
      localStorage.removeItem("lpp.onboard");

      setShowDeleteModal(false);
      setDeleteConfirmText("");
      navigate("/");

      alert("Your Little Paws Place account has been permanently deleted.");
    } catch (err) {
      console.error("Failed to delete profile:", err);
      alert(
        "Something went wrong while deleting your account. Please try again or contact support."
      );
    }
  };

  const statusLabel =
    status === "active"
      ? "Active"
      : status === "past_due"
      ? "Past due"
      : "No plan";

  const statusClass =
    status === "active"
      ? "account-status-pill active"
      : status === "past_due"
      ? "account-status-pill past-due"
      : "account-status-pill none";

  return (
    <div className="account-overview-wrapper">
      <div className="account-shell">
        {/* Header */}

        <PetPageHeader
  baseTitle="Account Overview"
  subtitle="Manage your SmartTag plan, phone, and primary tag."
/>


        {/* Body */}
        <div className="account-body">
          <Row className="g-3">
            {/* Plan summary + actions */}
            <Col md={7}>
              <section className="account-panel">
                <div className="account-label">SmartTag Plan</div>
                <p className="account-plan-text mb-2">{planSummaryText}</p>

                {maxTags > 0 && (
                  <div className="account-usage-row mb-3">
                    <div className="account-usage-label">
                      SmartTags in use
                    </div>
                    <div className="account-usage-count">
                      {usedTags}{" "}
                      {maxTags ? (
                        <span className="text-muted">/ {maxTags}</span>
                      ) : null}
                    </div>
                    {atLimit && (
                      <div className="account-usage-chip">
                        Plan at capacity
                      </div>
                    )}
                  </div>
                )}

                {/* Primary SmartTag actions */}
                <div className="d-flex flex-wrap gap-2 mt-2">
                  <Button
                    size="sm"
                    className="lpp-pill-btn-outline"
                    variant="outline-light"
                    onClick={handleManageCurrentTag}
                  >
                    Manage SmartTag
                  </Button>

                  <Button
                    size="sm"
                    className="lpp-pill-btn"
                    variant={atLimit ? "secondary" : "primary"}
                    disabled={atLimit}
                    onClick={handleLinkNewTag}
                  >
                    Add / Link SmartTag
                  </Button>
                </div>

                {/* Subtle account actions */}
                <p className="account-helper-text mt-3">
                  Need to update your billing details, change plans, or cancel
                  your subscription?{" "}
                  <button
                    type="button"
                    className="account-inline-link"
                    onClick={handleManagePlan}
                  >
                    Open subscription settings
                  </button>
                  .
                </p>

                <p className="account-helper-text mt-2">
                  Want to close your account?{" "}
                  <button
                    type="button"
                    className="account-inline-link-danger"
                    onClick={() => {
                      setDeleteConfirmText("");
                      setShowDeleteModal(true);
                    }}
                  >
                    Delete account
                  </button>
                  .
                </p>
              </section>
            </Col>

            {/* Account details */}
            <Col md={5}>
              <section className="account-panel">
                <div className="account-label">Account Details</div>

                <div className="account-detail-row">
                  <div className="account-detail-label">
                    <FontAwesomeIcon icon={faMobileScreen} className="me-1" />
                    Phone
                  </div>
                  <div className="account-detail-value">{phone}</div>
                </div>

                <div className="account-detail-row mt-3">
                  <div className="account-detail-label">
                    <FontAwesomeIcon icon={faMicrochip} className="me-1" />
                    Primary SmartTag
                  </div>
                  <div className="account-detail-value">
                    {tagId ? (
                      <Badge bg="dark" className="lpp-badge-tag">
                        {tagId}
                      </Badge>
                    ) : (
                      "Not linked"
                    )}
                  </div>
                </div>
              </section>
            </Col>
          </Row>
        </div>
      </div>

      {/* 🔴 Delete confirmation modal (now owned by AccountOverview) */}
      <Modal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setDeleteConfirmText("");
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            This will permanently delete your Little Paws Place account.
          </p>
          <p className="text-danger fw-bold">This cannot be undone.</p>
          <p className="small text-muted">
            To confirm, type <strong>DELETE</strong> in the box below and click
            &quot;Yes, delete my account&quot;.
          </p>
          <input
            type="text"
            className="form-control mt-2"
            placeholder="Type DELETE to confirm"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteConfirmText("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={deleteConfirmText !== "DELETE"}
            onClick={handleDeleteProfile}
          >
            Yes, delete my account
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
